import { FC } from "react";
import { Skeleton, Typography, TypographyProps } from "@mui/material";
import { ResourceType } from "../../../../../../../../../../services/cloudchipr.api";
import { getResourceTypeName } from "../../../../../../../../../../utils/helpers/resources/getResourceTypeName";
import { resourceViewIsWithoutFiltersSelector } from "../../../../../../../../../../store/account/selectors/current-account/resources/resourceViewIsWithoutFiltersSelector";
import { useAppSelector } from "../../../../../../../../../../store/hooks";
import { filterTemplatesEnabledSelector } from "../../../../../../../../../../store/account/selectors/current-account/resources/filterTemplatesEnabledSelector";

interface ResourcesCountProps {
  resourceType: ResourceType;
  dataCount?: number;
  isLoading?: boolean;
  totalDataCount?: number;
  filtersApplied?: boolean;
  color?: TypographyProps["color"];
}

export const ResourcesCount: FC<ResourcesCountProps> = ({
  resourceType,
  dataCount,
  isLoading,
  totalDataCount,
  filtersApplied,
  color,
}) => {
  const withoutFilters = useAppSelector((state) =>
    resourceViewIsWithoutFiltersSelector(state, resourceType),
  );
  const filterTemplatesEnabled = useAppSelector(filterTemplatesEnabledSelector);

  if (!isLoading && (totalDataCount === undefined || dataCount === undefined)) {
    return null;
  }

  if (!isLoading && !totalDataCount) {
    return (
      <Typography variant="body2" color={color}>
        No available {getResourceTypeName(resourceType)} found
      </Typography>
    );
  }

  if (!filtersApplied || withoutFilters || !filterTemplatesEnabled) {
    return (
      <Typography variant="body2" display="flex" color={color}>
        Showing all{" "}
        <Typography
          mx={0.5}
          component="span"
          fontWeight="bold"
          fontSize="inherit"
        >
          {isLoading ? LoadingState : totalDataCount}
        </Typography>{" "}
        available{" "}
        {getResourceTypeName(resourceType, {
          singular: totalDataCount === 1,
        })}
      </Typography>
    );
  }

  return (
    <Typography variant="body2" display="flex" color={color}>
      Showing
      <Typography
        mx={0.5}
        component="span"
        fontWeight="bold"
        fontSize="inherit"
      >
        {isLoading ? LoadingState : dataCount}
      </Typography>
      of
      <Typography
        mx={0.5}
        component="span"
        fontWeight="bold"
        fontSize="inherit"
      >
        {isLoading ? LoadingState : totalDataCount}
      </Typography>
      {`available ${getResourceTypeName(resourceType, {
        singular: totalDataCount === 1,
      })}`}
    </Typography>
  );
};

const LoadingState = (
  <Skeleton
    variant="text"
    height={20}
    width={30}
    sx={{ display: "inline-block" }}
  />
);
